import {
  useCallback,
  useMemo,
} from 'react';
import { useApiClient, useFetchInfinite } from '@marageti/z4-lib';
import type { AxiosRequestConfig } from 'axios';
import ZApiClient from '@marageti/z4-sdk/lib/client/ZApiClient';
import {
  EntityType,
  AnyEntityVersion,
  VersionsFilters,
  toEntityVersion,
} from '../types';

const DEFAULT_LIMIT = 100;

const getVersionsForEntity = (
  apiClient: ZApiClient,
  entityType: EntityType,
  id: string,
  config?: AxiosRequestConfig,
): Promise<AnyEntityVersion[]> => {
  switch (entityType) {
    case 'trip_requests':
      return apiClient.tripRequestsClient.getTripRequestVersions(id, config);
    case 'partners':
      return apiClient.partnersClient.getPartnerVersions(id, config);
    case 'agents':
      return apiClient.peopleClient.getAgentVersions(id, config);
    case 'travelers':
      return apiClient.peopleClient.getTravelerVersions(id, config);
    case 'users':
      return apiClient.authClient.getUserVersions(id, config);
    case 'reviews':
      return apiClient.reviewsClient.getReviewVersions(id, config);
    case 'trip_matches':
      return apiClient.instance.get<AnyEntityVersion[]>(`/v3/trip_matches/${id}/versions`, config);
    default:
      throw new Error(`Unknown entity type: ${entityType}`);
  }
};

const getVersionForEntity = (
  apiClient: ZApiClient,
  entityType: EntityType,
  id: string,
  updateId: string,
  config?: AxiosRequestConfig,
): Promise<AnyEntityVersion> => {
  switch (entityType) {
    case 'trip_requests':
      return apiClient.tripRequestsClient.getTripRequestVersion(id, updateId, config);
    case 'partners':
      return apiClient.partnersClient.getPartnerVersion(id, updateId, config);
    case 'agents':
      return apiClient.peopleClient.getAgentVersion(id, updateId, config);
    case 'travelers':
      return apiClient.peopleClient.getTravelerVersion(id, updateId, config);
    case 'users':
      return apiClient.authClient.getUserVersion(id, updateId, config);
    case 'reviews':
      return apiClient.reviewsClient.getReviewVersion(id, updateId, config);
    case 'trip_matches':
      return apiClient.instance.get<AnyEntityVersion>(`/v3/trip_matches/${id}/versions/${updateId}`, config);
    default:
      throw new Error(`Unknown entity type: ${entityType}`);
  }
};

export const useEntityVersions = (filters: VersionsFilters) => {
  const apiClient = useApiClient();

  const getKey = useCallback(
    (pageIndex: number, previousPageData: AnyEntityVersion[] | null) => {
      if (!filters.entityType || !filters.id) return null;
      if (filters.all) {
        if (pageIndex > 0) return null;
        return ['getVersions', filters.entityType, filters.id, 'all'];
      }
      if (previousPageData && previousPageData.length < DEFAULT_LIMIT) return null;
      const offset = previousPageData?.length
        ? previousPageData[previousPageData.length - 1].updateId
        : undefined;
      return ['getVersions', filters.entityType, filters.id, DEFAULT_LIMIT, offset];
    },
    [filters.entityType, filters.id, filters.all],
  );

  const fetcher = useCallback(
    ([, entityType, id, limitOrAll, offset]: (string | number | undefined)[]) => (
      getVersionsForEntity(
        apiClient,
        entityType as EntityType,
        id as string,
        {
          params: {
            all: limitOrAll === 'all' || undefined,
            limit: limitOrAll === 'all' ? undefined : limitOrAll,
            offset,
          },
        },
      )
    ),
    [apiClient],
  );

  const {
    data,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useFetchInfinite<AnyEntityVersion[]>(getKey, fetcher, {
    revalidateFirstPage: false,
    revalidateOnFocus: false,
  });

  const rawVersions = useMemo(() => data?.flat() ?? [], [data]);
  const versions = useMemo(() => rawVersions.map(toEntityVersion), [rawVersions]);

  const lastPage = data?.[data.length - 1];
  const hasMore = !filters.all && !!lastPage && lastPage.length >= DEFAULT_LIMIT;

  const loadMore = useCallback(() => {
    if (!isValidating) setSize(size + 1);
  }, [isValidating, setSize, size]);

  const fetchVersion = useCallback(async (
    entityType: EntityType,
    id: string,
    updateId: string,
  ): Promise<AnyEntityVersion | null> => {
    try {
      return await getVersionForEntity(apiClient, entityType, id, updateId);
    } catch {
      return null;
    }
  }, [apiClient]);

  return {
    versions,
    rawVersions,
    isLoading: isLoading || isValidating,
    error: error ?? null,
    hasMore,
    loadMore,
    fetchVersion,
  };
};

export default useEntityVersions;
